import { logger } from '../../utils/logger.js'
import { resizeImageToDataUrl } from '../../utils/image-resize.js'
import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import {
  UserStorageService,
  type UserProfile,
  type CreateUserData,
} from '../../storage/user-storage'
import { authService } from '../../services/auth/auth-service.js'
import { updateUserAttributes } from '../../services/auth/cognito-api.js'
import { type AuthState, GUEST_STATE } from '../../services/auth/auth-context.js'
import { syncListOnNavigate } from '../../storage/sync-manager.js'
import '../auth/auth-login-prompt.js'

const log = logger.create('profile')

// Material Web Components
import '@material/web/textfield/filled-text-field.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import '@material/web/button/filled-button.js'
import '@material/web/button/text-button.js'
import '@material/web/icon/icon.js'

interface ProfileFormData {
  firstName: string
  lastName: string
  birthday: string
  email: string
  phone: string
  emergencyContact: {
    name: string
    phone: string
    relationship: string
  }
  avatar?: string | undefined
}

@customElement('cairn-profile')
export class CairnProfile extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
      overflow: hidden;
    }

    .profile-header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex: 0 0 auto; /* Fixed height header */
    }

    .profile-page {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow-y: auto;
      padding-bottom: var(--cairn-spacing-lg, 24px);
      min-height: 0; /* Important for flex child scrolling */
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
    }

    .back-button:hover {
      background: rgba(59, 130, 246, 0.04);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .profile-header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1;
    }

    .profile-container {
      max-width: 600px;
      margin: 0 auto;
      padding: var(--cairn-spacing-lg, 24px);
    }

    .profile-title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .settings-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
    }

    .settings-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-primary-color, #1976d2);
    }

    .avatar-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: var(--cairn-spacing-xl, 32px);
    }

    .avatar {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 48px;
      font-weight: 500;
      margin-bottom: var(--cairn-spacing-md, 16px);
      position: relative;
      overflow: hidden;
    }

    .avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-upload {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 40px;
      height: 40px;
      background: var(--cairn-primary-color, #1976d2);
      border: 3px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .avatar-upload:hover {
      background: var(--cairn-primary-color-dark, #1565c0);
      transform: scale(1.05);
    }

    .avatar-upload input {
      display: none;
    }

    .form-section {
      background: var(--cairn-surface-color, #ffffff);
      border-radius: var(--cairn-border-radius-lg, 12px);
      padding: var(--cairn-spacing-xl, 32px);
      margin-bottom: var(--cairn-spacing-lg, 24px);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .section-title {
      font-size: var(--cairn-text-size-lg, 18px);
      font-weight: var(--cairn-font-weight-semibold, 500);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-lg, 24px) 0;
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .form-grid {
      display: grid;
      gap: var(--cairn-spacing-md, 16px);
    }

    .form-grid.two-column {
      grid-template-columns: 1fr 1fr;
    }

    /* Material Web Components styling - inherit from global theme tokens */
    md-outlined-text-field {
      width: 100%;
      --md-outlined-text-field-container-height: 48px;
      --md-outlined-text-field-container-shape: var(--cairn-border-radius, 8px);
    }

    md-filled-select,
    md-outlined-select {
      width: 100%;
    }

    md-filled-select {
      --md-filled-select-text-field-container-height: 48px;
      --md-filled-select-text-field-container-shape: var(--cairn-border-radius, 8px);
    }

    md-outlined-select {
      --md-outlined-select-text-field-container-height: 48px;
      --md-outlined-select-text-field-container-shape: var(--cairn-border-radius, 8px);
    }

    md-filled-button {
      width: 100%;
      --md-filled-button-container-height: 48px;
      --md-filled-button-container-shape: var(--cairn-border-radius, 8px);
    }

    .emergency-contact {
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-lg, 24px);
      background: var(--cairn-surface-color, #ffffff);
    }

    .emergency-contact-title {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-md, 16px) 0;
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
    }

    /* Mobile responsiveness */
    @media (max-width: 640px) {
      .profile-container {
        max-width: none;
        margin: 0;
        padding: var(--cairn-spacing-md, 16px);
      }

      .form-grid.two-column {
        grid-template-columns: 1fr;
      }

      .form-section {
        padding: var(--cairn-spacing-lg, 24px);
      }

      .profile-header {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }

    /* Hide header on desktop - title shown in main app header */
    @media (min-width: 769px) {
      .profile-header {
        display: none;
      }
    }

    .auto-save-indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--cairn-spacing-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .auto-save-indicator::before {
      content: '';
      width: 16px;
      height: 16px;
      border: 2px solid var(--cairn-primary-color, #3b82f6);
      border-top: 2px solid transparent;
      border-radius: 50%;
      margin-right: var(--cairn-spacing-sm, 8px);
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  `

  @property({ type: Object })
  userProfile: UserProfile | null = null

  @state()
  private formData: ProfileFormData = {
    firstName: '',
    lastName: '',
    birthday: '',
    email: '',
    phone: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: 'Family',
    },
  }

  @state()
  private isLoading = false

  @state()
  private errorMessage = ''

  @state()
  private authState: AuthState = { ...GUEST_STATE }

  private userStorage = new UserStorageService()
  private autoSaveTimeout: number | null = null
  private lastSavedForm: string | undefined
  private unsubAuth?: () => void

  override connectedCallback(): void {
    super.connectedCallback()
    this.authState = authService.getState()
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
      if (state.status === 'authenticated') {
        void this.loadUserProfile()
      }
    })
    void this.loadMaterialIcons()
    void this.loadUserProfile()
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()

    // Clear any pending auto-save timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
      this.autoSaveTimeout = null
    }

    // Auto-save when leaving the page if there are changes
    if (this.hasUnsavedChanges()) {
      void this.handleSave()
    }
  }

  private formSnapshot(): string {
    return JSON.stringify(this.formData)
  }

  private hasUnsavedChanges(): boolean {
    return this.formSnapshot() !== this.lastSavedForm
  }

  private scheduleAutoSave(): void {
    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    // Schedule auto-save after 2 seconds of inactivity
    this.autoSaveTimeout = window.setTimeout(() => {
      const changed = this.hasUnsavedChanges()
      log.debug('Auto-save check, hasChanges:', changed)
      if (changed) {
        void this.handleSave()
      }
    }, 2000)
  }

  private async loadMaterialIcons(): Promise<void> {
    try {
      // Create a style element with the Material Icons font
      const materialStyle = document.createElement('style')
      materialStyle.textContent = `
        @font-face {
          font-family: 'Material Symbols Outlined';
          font-style: normal;
          font-weight: 400;
          src: url('/node_modules/@material-symbols/font-400/material-symbols-outlined.woff2') format('woff2');
        }

        .material-icons {
          font-family: 'Material Symbols Outlined';
          font-weight: normal;
          font-style: normal;
          font-size: 24px;
          line-height: 1;
          letter-spacing: normal;
          text-transform: none;
          display: inline-block;
          white-space: nowrap;
          word-wrap: normal;
          direction: ltr;
          -webkit-font-feature-settings: 'liga';
          -webkit-font-smoothing: antialiased;
        }
      `
      this.shadowRoot?.appendChild(materialStyle)
    } catch (error) {
      log.error('Failed to load Material Symbols CSS:', error)
    }
  }

  private async loadUserProfile(): Promise<void> {
    try {
      await syncListOnNavigate('user').catch(() => {})
      const userId = this.authState.userId
      const result = await this.userStorage.getUserProfile(userId)

      if (result.success && result.data) {
        this.userProfile = result.data
        this.populateFormData(result.data)
        this.lastSavedForm = this.formSnapshot()
      } else if (this.authState.status === 'authenticated') {
        // Pre-populate from auth claims (Cognito attributes) for newly authenticated users
        this.formData = {
          ...this.formData,
          firstName: this.authState.givenName ?? '',
          lastName: this.authState.familyName ?? '',
          email: this.authState.email ?? '',
          phone: this.authState.phoneNumber ?? '',
          birthday: this.authState.birthdate ?? '',
          avatar: this.authState.picture,
        }
        this.lastSavedForm = this.formSnapshot()
      } else {
        log.debug('No existing user profile found')
      }
    } catch (error) {
      log.error('Error loading user profile:', error)
      this.errorMessage = 'Failed to load profile data'
    }
  }

  private populateFormData(profile: UserProfile): void {
    const firstName = profile.givenName ?? profile.displayName.split(' ')[0]
    const lastName = profile.familyName ?? profile.displayName.split(' ').slice(1).join(' ')

    this.formData = {
      firstName,
      lastName,
      birthday: '', // TODO: load from Cognito attributes
      email: profile.email,
      phone: profile.phone ?? '',
      emergencyContact: {
        name: profile.emergencyContact?.name ?? '',
        phone: profile.emergencyContact?.phone ?? '',
        relationship: profile.emergencyContact?.relationship ?? 'Family',
      },
      avatar: profile.picture,
    }
  }

  private handleInputChange(field: keyof ProfileFormData, value: string): void {
    this.formData = { ...this.formData, [field]: value }
    this.scheduleAutoSave()
    this.requestUpdate()
  }

  private handleEmergencyContactChange(
    field: keyof ProfileFormData['emergencyContact'],
    value: string
  ): void {
    this.formData = {
      ...this.formData,
      emergencyContact: {
        ...this.formData.emergencyContact,
        [field]: value,
      },
    }
    this.scheduleAutoSave()
    this.requestUpdate()
  }

  private async handleSave(): Promise<void> {
    this.isLoading = true
    this.errorMessage = ''

    try {
      const displayName = `${this.formData.firstName} ${this.formData.lastName}`.trim()

      // Save to Cognito
      const token = authService.getAccessToken()
      log.debug('Saving profile, has access token:', !!token)
      if (token) {
        const attributes: Record<string, string> = {}
        if (this.formData.firstName.trim())
          attributes['given_name'] = this.formData.firstName.trim()
        if (this.formData.lastName.trim()) attributes['family_name'] = this.formData.lastName.trim()
        if (displayName) attributes['name'] = displayName
        if (this.formData.email.trim()) attributes['email'] = this.formData.email.trim()
        if (this.formData.phone.trim()) {
          let phone = this.formData.phone.trim().replace(/[\s\-().]/g, '')
          if (!phone.startsWith('+')) phone = '+1' + phone
          attributes['phone_number'] = phone
        }
        if (this.formData.birthday) attributes['birthdate'] = this.formData.birthday
        // picture attribute is URL-only (2048 char limit) — needs S3 upload

        if (Object.keys(attributes).length > 0) {
          await updateUserAttributes(token, attributes)
          await authService.refreshSession()
        }
      }

      // Save to local storage
      if (this.userProfile) {
        const updates: Record<string, unknown> = {
          displayName,
          givenName: this.formData.firstName.trim() || undefined,
          familyName: this.formData.lastName.trim() || undefined,
        }
        if (this.formData.phone.trim()) {
          updates.phone = this.formData.phone.trim()
        }
        const ec = this.formData.emergencyContact
        if (ec.name.trim() && ec.phone.trim()) {
          updates.emergencyContact = {
            name: ec.name.trim(),
            phone: ec.phone.trim(),
            relationship: ec.relationship.trim() || 'Family',
          }
        }
        if (this.authState.picture) {
          updates.picture = this.authState.picture
        }

        const result = await this.userStorage.updateUser(this.userProfile.id, updates)

        if (!result.success) {
          this.errorMessage = result.error?.message ?? 'Failed to update profile'
        }
      } else {
        const createData: CreateUserData = {
          email: this.formData.email,
          displayName,
          provider: 'email',
        }

        const result = await this.userStorage.createUser(createData)

        if (result.success) {
          this.userProfile = result.data ?? null
        } else {
          this.errorMessage = result.error?.message ?? 'Failed to create profile'
        }
      }
      this.lastSavedForm = this.formSnapshot()
    } catch (error) {
      log.error('Error saving profile:', error)
      this.errorMessage = 'An unexpected error occurred'
    } finally {
      this.isLoading = false
    }
  }

  private handleAvatarUpload(event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Please select an image file.'
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage = 'Image must be under 10MB.'
      return
    }

    void this.resizeAndSetAvatar(file)
  }

  private async resizeAndSetAvatar(file: File): Promise<void> {
    try {
      const dataUrl = await resizeImageToDataUrl(file)
      this.formData = { ...this.formData, avatar: dataUrl }
      this.errorMessage = ''
      this.requestUpdate()
      this.scheduleAutoSave()
    } catch {
      this.errorMessage = 'Image is too large even after compression. Try a simpler image.'
    }
  }

  private getInitials(): string {
    const first = this.formData.firstName.charAt(0).toUpperCase()
    const last = this.formData.lastName.charAt(0).toUpperCase()
    return first + last || this.formData.email.charAt(0).toUpperCase()
  }

  override render(): unknown {
    return html`
      ${this.authState.status !== 'authenticated'
        ? html`
            <div class="profile-page">
              <auth-login-prompt></auth-login-prompt>
            </div>
          `
        : this.renderProfileForm()}
    `
  }

  private renderProfileForm(): unknown {
    return html`
      <div class="profile-page">
        <div class="profile-container">
          <div class="avatar-section">
            <div class="avatar">
              ${this.formData.avatar
                ? html`<img src="${this.formData.avatar}" alt="Profile picture" />`
                : html`<span>${this.getInitials()}</span>`}
              ${!this.formData.avatar
                ? html`<label class="avatar-upload">
                    <input type="file" accept="image/*" @change=${this.handleAvatarUpload} />
                    <span class="material-icons">add_a_photo</span>
                  </label>`
                : nothing}
            </div>
          </div>

          <div class="form-section">
            <h2 class="section-title">
              <span class="material-icons">person</span>
              Personal Information
            </h2>
            <div class="form-grid">
              <div class="form-grid two-column">
                <md-outlined-text-field
                  label="First Name"
                  .value=${this.formData.firstName}
                  @input=${(e: Event): void =>
                    this.handleInputChange('firstName', (e.target as HTMLInputElement).value)}
                ></md-outlined-text-field>
                <md-outlined-text-field
                  label="Last Name"
                  .value=${this.formData.lastName}
                  @input=${(e: Event): void =>
                    this.handleInputChange('lastName', (e.target as HTMLInputElement).value)}
                ></md-outlined-text-field>
              </div>

              <div class="form-grid two-column">
                <md-outlined-text-field
                  type="date"
                  label="Birthday"
                  .value=${this.formData.birthday}
                  @input=${(e: Event): void =>
                    this.handleInputChange('birthday', (e.target as HTMLInputElement).value)}
                ></md-outlined-text-field>
                <md-outlined-text-field
                  type="tel"
                  label="Phone"
                  .value=${this.formData.phone}
                  @input=${(e: Event): void =>
                    this.handleInputChange('phone', (e.target as HTMLInputElement).value)}
                ></md-outlined-text-field>
              </div>

              <md-outlined-text-field
                type="email"
                label="Email"
                .value=${this.formData.email}
                @input=${(e: Event): void =>
                  this.handleInputChange('email', (e.target as HTMLInputElement).value)}
              ></md-outlined-text-field>
            </div>
          </div>

          <div class="form-section">
            <h2 class="section-title">
              <span class="material-icons">local_hospital</span>
              Emergency Contact
            </h2>
            <div class="form-grid">
              <md-outlined-text-field
                label="Contact Name"
                .value=${this.formData.emergencyContact.name}
                @input=${(e: Event): void =>
                  this.handleEmergencyContactChange('name', (e.target as HTMLInputElement).value)}
              ></md-outlined-text-field>

              <div class="form-grid two-column">
                <md-outlined-text-field
                  type="tel"
                  label="Phone Number"
                  .value=${this.formData.emergencyContact.phone}
                  @input=${(e: Event): void =>
                    this.handleEmergencyContactChange(
                      'phone',
                      (e.target as HTMLInputElement).value
                    )}
                ></md-outlined-text-field>
                <md-outlined-select
                  label="Relationship"
                  .value=${this.formData.emergencyContact.relationship}
                  @change=${(e: Event): void =>
                    this.handleEmergencyContactChange(
                      'relationship',
                      (e.target as HTMLElement & { value: string }).value
                    )}
                >
                  <md-select-option value="Family">
                    <div slot="headline">Family</div>
                  </md-select-option>
                  <md-select-option value="Spouse">
                    <div slot="headline">Spouse</div>
                  </md-select-option>
                  <md-select-option value="Parent">
                    <div slot="headline">Parent</div>
                  </md-select-option>
                  <md-select-option value="Sibling">
                    <div slot="headline">Sibling</div>
                  </md-select-option>
                  <md-select-option value="Friend">
                    <div slot="headline">Friend</div>
                  </md-select-option>
                  <md-select-option value="Other">
                    <div slot="headline">Other</div>
                  </md-select-option>
                </md-outlined-select>
              </div>
            </div>
          </div>

          ${this.errorMessage
            ? html`
                <div
                  style="color: var(--cairn-error-color, #dc2626); margin-bottom: 16px; text-align: center;"
                >
                  ${this.errorMessage}
                </div>
              `
            : ''}

          <!-- Auto-save enabled - no save button needed -->
          ${this.isLoading
            ? html`
                <div class="auto-save-indicator">
                  <span>Saving changes...</span>
                </div>
              `
            : ''}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-profile': CairnProfile
  }
}
